import { useState, useMemo } from 'react'
import { InsuranceData, LiabilitySectionData } from '../../../dtos/applicationData'
import FileData from '../../../dtos/fileData'
import { useApplication } from '../../../context/application'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultSubtitle,
  DefaultTitle,
  FormContainer,
  OptionButtonsContainer
} from '../../../components/application/defaultComponents'
import { FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import LastInsurance from '../../../components/application/formComponents/LastInsurance'
import Button from '../../../components/basic/Button'
import OptionButton from '../../../components/basic/OptionButton'
import { validateDateField } from 'src/utils/formDataManipulation'

const newInsuranceData = {
  companyName: '',
  deductible: 0,
  expirationDate: '',
  annualPremium: undefined,
  isTailCoverage: undefined,
  isCurrent: undefined,
  retroactiveDate: '',
  policyFile: undefined
}

const Main = () => {
  const lastInsuranceErrorCodes = ['EC253', 'EC254', 'EC255', 'EC256', 'EC257', 'EC258', 'EC259', 'EC260', 'EC261', 'EC262']
  const { application, saveApplication } = useApplication()
  const [liabilityData, setLiabilityData] = useState<LiabilitySectionData>(
    () => !!application.liabilitySection ? { ...application.liabilitySection } : {}
  )
  const [insurances, setInsurances] = useState<InsuranceData[]>(
    () => application?.liabilitySection?.insurances ?? [newInsuranceData]
  )
  const [secondaryInsurance, setSecondaryInsurance] = useState<boolean>(() => {
    return !!application?.liabilitySection?.insurances && 
      application?.liabilitySection?.insurances.length === 2
  });
  const [showErrors, setShowErrors] = useState(false)

  const handleOptionChange = (option: boolean) => {
    option === false ?
      setInsurances([]) :
      setInsurances([newInsuranceData])
    setLiabilityData({ ...liabilityData, isFirmRecentlyInsured: option })
  }

  const handleInsuranceChange = (insuranceIndex: number, insurance: InsuranceData) => {
    setInsurances(insurances.map( (e, idx) => {
      if (insuranceIndex ===  idx) 
        return insurance
      return e
    }))
  }

  const handleUploadFile = async (fileData: FileData[] | undefined, insuranceIndex: number) => {
    const insuranceCopy = { ...insurances[insuranceIndex] }

    if(!fileData) {
      insuranceCopy['policyFile'] = undefined
    } else {
      insuranceCopy['policyFile'] = [...(insuranceCopy?.policyFile ?? []), ...fileData]
    }
    
    try {
      await saveApplication({
        ...application,
        stoppedAt: '/insurance/last-insurances',
        liabilitySection: {
          ...application.liabilitySection,
          ...liabilityData,
          insurances: insurances.map((insurance, index) => index === insuranceIndex ? insuranceCopy : insurance)
        }
      })
      
      handleInsuranceChange(insuranceIndex, insuranceCopy)
    } catch(_) {}
  }

  const toggleSecondaryInsurance = (option: boolean) => {
    option === false ? 
      setInsurances([insurances[0]]) :
      setInsurances([...insurances, newInsuranceData])
    setSecondaryInsurance(option)
  }

  const updatedApplicationData = () => ({
    ...application,
    currentPage: window.location.pathname,
    liabilitySection: { ...application.liabilitySection, ...liabilityData, insurances }
  })

  const goBackRoute = application?.attorneysSection?.hasICOCAttorneys === true ?
    '/attorneys/ic-oc' : 
    '/attorneys/relationships'

  const validatePage = useMemo(() => {
    if(liabilityData?.isFirmRecentlyInsured === null || liabilityData?.isFirmRecentlyInsured === undefined) {
      return '' //'main_question'
    } else if(liabilityData?.isFirmRecentlyInsured === true && (
        insurances?.[0]?.isCurrent === null || 
        insurances?.[0]?.isCurrent === undefined)
    ) {
      return '' //'current_insurance_question'
    }
    return ''
  }, [insurances, liabilityData?.isFirmRecentlyInsured])
    
  const scrollIfInvalid = () => {
    if(validatePage !== '') {
      setShowErrors(true)
      document.getElementById(validatePage)?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle id="main_question">Has {application.firmSection?.firmName} been insured within the last 5 years?</DefaultTitle>
        <FormContainer>
          <OptionButtonsContainer>
            <div>
              <OptionButton
                checked={liabilityData.isFirmRecentlyInsured === true}
                onChange={() => handleOptionChange(true)}
                isIncorrect={showErrors && validatePage === 'main_question'}
              >
                Yes
              </OptionButton>
              <OptionButton
                checked={liabilityData.isFirmRecentlyInsured === false}
                onChange={() => handleOptionChange(false)}
                isIncorrect={showErrors && validatePage === 'main_question'}
              >
                No
              </OptionButton>
            </div>
            {showErrors && validatePage === 'main_question' ? (
              <FormInputErrorMessage>
                This is a required field
              </FormInputErrorMessage>
            ) : null}
          </OptionButtonsContainer>
          {liabilityData?.isFirmRecentlyInsured === true ?
            <>
              <DefaultSubtitle style={{ margin: '32px 0 0 0' }}>PLEASE ADD INSURERS WITHIN THE LAST FIVE YEARS</DefaultSubtitle>
              <LastInsurance
                title='Most Recent Insurance'
                insurance={insurances[0]}
                setInsurance={(insurance) => handleInsuranceChange(0, insurance)}
                insuranceNumber={1}
                handleUploadFile={fileData => handleUploadFile(fileData, 0)}
                isIncorrect={showErrors && validatePage === 'current_insurance_question'}
                errorCodes={lastInsuranceErrorCodes}
              />
              {secondaryInsurance === true ?
                <>
                  <LastInsurance
                    title='Prior Insurance (Optional)'
                    insurance={insurances[1]}
                    setInsurance={(insurance) => handleInsuranceChange(1, insurance)}
                    insuranceNumber={2}
                    isSecondary={true}
                    handleUploadFile={fileData => handleUploadFile(fileData, 1)}
                    errorCodes={lastInsuranceErrorCodes}
                  />
                  <Button
                    variant="error"
                    onClick={() => toggleSecondaryInsurance(false)}
                    style={{ marginTop: '60px', maxWidth: '360px' }}
                  >
                    Remove insurer
                  </Button>
                </>
                :
                <Button
                  variant="secondary"
                  onClick={() => toggleSecondaryInsurance(true)}
                  style={{ marginTop: '60px', maxWidth: '360px' }}
                >
                  Add another insurer
                </Button>
              }
            </>
            : undefined}
        </FormContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute={goBackRoute}
        nextRoute='/insurance/past-liability'
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={scrollIfInvalid}
        isFormValid={validatePage === ''}
      />
    </ApplicationBodyContainer>
  )
}

export default Main